import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

const nodeTouchesIframe = (node, iframeEl) => {
  if (!node || !iframeEl) {
    return false;
  }
  if (node === iframeEl) {
    return true;
  }
  const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  if (!element) {
    return false;
  }
  return element === iframeEl || iframeEl.contains(element);
};

export const clearCourseIframeSelection = (iframeEl) => {
  if (typeof window === 'undefined' || !iframeEl || typeof window.getSelection !== 'function') {
    return;
  }

  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  let touches = nodeTouchesIframe(selection.anchorNode, iframeEl)
    || nodeTouchesIframe(selection.focusNode, iframeEl);

  if (!touches) {
    try {
      for (let i = 0; i < selection.rangeCount; i += 1) {
        const range = selection.getRangeAt(i);
        if (typeof range.intersectsNode === 'function' && range.intersectsNode(iframeEl)) {
          touches = true;
          break;
        }
      }
    } catch (error) {
      touches = false;
    }
  }

  if (touches) {
    selection.removeAllRanges();
  }
};

const blurIframeIfFocused = (iframeEl) => {
  if (iframeEl && document.activeElement === iframeEl) {
    iframeEl.blur();
  }
};

/**
 * Keep the course iframe from staying selected while it loads.
 * This course navigates twice (shell, then the lesson), so selection is
 * cleared on open, on each load, and once shortly after each load.
 */
export const useCourseIframeSelection = (isOpen, iframeUrl) => {
  const iframeRef = useRef(null);
  const followUpTimerRef = useRef(0);

  const clearSelection = useCallback(() => {
    const iframe = iframeRef.current;
    blurIframeIfFocused(iframe);
    clearCourseIframeSelection(iframe);
  }, []);

  useEffect(() => () => {
    window.clearTimeout(followUpTimerRef.current);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    clearSelection();
    const timer = window.setTimeout(clearSelection, 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, iframeUrl, clearSelection]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const onSelectionChange = () => {
      clearCourseIframeSelection(iframeRef.current);
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [isOpen]);

  const handleIframeLoad = useCallback(() => {
    clearCourseIframeSelection(iframeRef.current);
    window.clearTimeout(followUpTimerRef.current);
    followUpTimerRef.current = window.setTimeout(() => {
      clearCourseIframeSelection(iframeRef.current);
    }, 300);
  }, []);

  return { iframeRef, handleIframeLoad };
};
